import React, { useEffect, useState } from "react";
import { X, Download, Smartphone, Share, PlusSquare, Sparkles, ArrowDownToLine } from "lucide-react";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) { setInstalled(true); return; }

    const lastShown = parseInt(localStorage.getItem("icard_install_last") || "0", 10);
    const dismissed = lastShown && (Date.now() - lastShown) < 7 * 24 * 60 * 60 * 1000;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissed) {
        setTimeout(() => setShow(true), 10_000);
      }
    };
    const onInstalled = () => { setInstalled(true); setShow(false); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback prompt (no beforeinstallprompt)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && !dismissed && !isStandalone) {
      setTimeout(() => setShow(true), 10_000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) {
      if (isIOS) { setShowSteps((visible) => !visible); return; }
      setShow(false);
      return;
    }
    deferred.prompt();
    await deferred.userChoice;
    setShow(false);
    setDeferred(null);
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem("icard_install_last", String(Date.now()));
  };

  if (installed || !show) return null;

  return (
    <div data-testid="install-prompt" className="fixed bottom-[calc(0.75rem+env(safe-area-inset-bottom))] left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 sm:max-w-[24rem] z-[100] fade-in">
      <div className="relative overflow-hidden rounded-[1.6rem] border border-emerald-900/10 bg-white/95 p-4 text-slate-900 shadow-[0_22px_70px_-24px_rgba(15,67,53,0.42)] backdrop-blur-xl sm:p-5">
        <div className="pointer-events-none absolute -right-12 -top-14 h-36 w-36 rounded-full bg-emerald-100/80 blur-2xl" />
        <div className="relative flex items-start gap-3.5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600 via-green-600 to-lime-500 text-white shadow-lg shadow-emerald-900/15">
            <Smartphone size={23} strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.16em] text-emerald-700">
              <Sparkles size={12} /> iCaard, close at hand
            </div>
            <h2 className="mt-1 font-display text-lg font-extrabold leading-tight tracking-tight text-slate-900">Add iCaard to your home screen</h2>
            <p className="mt-1 text-sm leading-5 text-slate-600">Open tickets, your card and services in one tap.</p>
          </div>
          <button aria-label="Dismiss install prompt" data-testid="install-prompt-close" onClick={dismiss} className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
            <X size={18} />
          </button>
        </div>

        {isIOS && !deferred && showSteps && (
          <div className="relative mt-4 space-y-2 rounded-2xl border border-emerald-900/10 bg-emerald-50/80 p-3.5 text-sm text-slate-700" data-testid="install-prompt-ios-steps">
            <div className="font-bold text-slate-900">Install in Safari</div>
            <div className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-bold text-emerald-700">1</span>Tap <Share size={15} className="text-blue-600" /> Share.</div>
            <div className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-bold text-emerald-700">2</span>Choose <PlusSquare size={15} className="text-slate-600" /> Add to Home Screen.</div>
            <div className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-bold text-emerald-700">3</span>Tap Add to finish.</div>
          </div>
        )}

        <div className="relative mt-4 flex items-center gap-2">
          <button data-testid="install-prompt-install-btn" onClick={install} className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white shadow-md shadow-emerald-900/15 transition hover:bg-emerald-800 active:scale-[0.99]">
            {isIOS && !deferred ? <><Share size={17} /> {showSteps ? "Hide steps" : "Show me how"}</> : <><ArrowDownToLine size={17} /> Install iCaard</>}
          </button>
          <button data-testid="install-prompt-later-btn" onClick={dismiss} className="min-h-12 rounded-xl px-3 text-sm font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800">Not now</button>
        </div>
        <div className="relative mt-2 flex items-center justify-center gap-1.5 text-[11px] font-medium text-slate-400">
          <Download size={12} /> Free · Takes a few seconds
        </div>
      </div>
    </div>
  );
}
